import store from 'store';
import { dataActions, UserRegistrationInfo, sessionActions, requestsActions } from 'reducers';
import { produce } from 'immer';
import { API_ADDRESS, post, put, get, del } from 'lib/communication';
import { RooferInfoResponse, UserInfoResponse, LoginResponse, Response, ErrorResponse, AuthUserResponse, UserUpdateResponse } from './communicationTypes';
import { RegisteredUserData, RooferInfo, User, Param, ServiceWorkType, Permissions, UserDeliveryAddress, ShutdownHours } from 'lib/types';
import { Dispatch } from 'react';
import { sessionService, userService } from 'services';
import { GA_userLoggedIn } from 'lib/gtm-utils';

const setUserInfoFetching = (isFetching: boolean) => {
    store.dispatch(dataActions.setUserInfoFetching(isFetching));
};

const mapLoginResponseToUser = (loginResponse: LoginResponse): User => {
    const { user, jwt, environment } = loginResponse;
    return {
        id: user.id,
        login: user.login,
        username: user.username,
        email: user.email,
        role: user.role,
        authEnabled: user.isAuthEnabled === '1',
        permissions: new Permissions(user.permissions),
        name: user.name,
        phone: user.phone,
        surname: user.surname,
        token: jwt.token,
        tokenExpiration: jwt.tokenExpiration,
        company: user.company,
        companyIsod: user.companyIsod,
        guardian: user.guardian,
        guardianMail: user.guardianMail,
        guardianPhone: user.guardianPhone,
        mag: user.mag,
        logo: user.logo,
        forcePwdChange: user.forcePwdChange !== '0',
        environment,
        promotionalProgramEnroll: user.promotionalProgramEnroll,
        promotionTermsAccept: user.promotionTermsAccept,
        promotionTermsChange: user.promotionTermsChange,
        wydr: user.wydr,
        currency: user.currency,
        params: user.params
    };
};

const mapAuthResponseToUser = (authUserResponse: AuthUserResponse): User => {
    const { user, environment } = authUserResponse;
    const { token, tokenExpiration } = sessionService.getToken()!;
    return {
        id: user.id,
        login: user.login,
        username: user.username,
        email: user.email,
        role: user.role,
        authEnabled: user.isAuthEnabled === '1',
        permissions: new Permissions(user.permissions),
        name: user.name,
        phone: user.phone,
        surname: user.surname,
        environment,
        token,
        tokenExpiration,
        company: user.company,
        companyIsod: user.companyIsod,
        guardian: user.guardian,
        guardianMail: user.guardianMail,
        guardianPhone: user.guardianPhone,
        mag: user.mag,
        logo: user.logo,
        forcePwdChange: user.forcePwdChange !== '0',
        promotionalProgramEnroll: user.promotionalProgramEnroll,
        promotionTermsAccept: user.promotionTermsAccept,
        promotionTermsChange: user.promotionTermsChange,
        wydr: user.wydr,
        currency: user.currency,
        params: user.params
    };
};

const fetchUser = async (): Promise<User | null> => {
    const route: string = 'user/authUser';
    try {
        const data = await get<AuthUserResponse>(`${API_ADDRESS}${route}`);
        if (data.ok && data.parsedBody) return mapAuthResponseToUser(data.parsedBody);
        return null;
    } catch (error) {
        console.error(error);
        return null;
    }
};

export const authUser = async (dispatch: Dispatch<any>) => {
    if (sessionService.getToken() === null) {
        dispatch(sessionActions.setIsFetchingUser(false));
        return;
    }
    const user = await fetchUser();
    if (store.getState().requests.tooManyAttempts) {
        dispatch(sessionActions.setIsFetchingUser(false));
        return;
    }
    if (user === null) {
        dispatch(requestsActions.setInvalidToken(true));
        return;
    }
    dispatch(sessionActions.setUser(user));
    dispatch(sessionActions.setIsFetchingUser(false));
};

export const getUserInfo = async (
    login: string
): Promise<boolean> => {
    const route: string = 'user/getUserData';
    setUserInfoFetching(true);
    try {
        const data = await post<UserInfoResponse>(`${API_ADDRESS}${route}`, { login });

        if (data.ok && data.parsedBody) {
            const user = data.parsedBody;
            const state = store.getState();
            let setUser: UserRegistrationInfo;
            if (state.data.userInfo.userInfo !== null) {
                setUser = produce(state.data.userInfo.userInfo, (draft) => {
                    draft.company.name = user.company;
                    draft.company.regon = String(user.regon);
                    draft.user.firstName = user.name;
                    draft.user.lastName = user.surname;
                    draft.user.street = user.street;
                    draft.user.buildingNo = user.building;
                    draft.user.apartmentNo = user.apartment;
                    draft.user.postalCode = user.postalCode;
                    draft.user.town = user.town;
                    draft.user.nip = String(user.nip);
                    draft.user.pesel = user.pesel;
                    draft.user.treasuryOffice = user.taxOffice;
                    draft.user.phone = String(user.phone);
                    draft.user.email = user.email;
                });
            } else {
                setUser = {
                    user: {
                        firstName: user.name,
                        lastName: user.surname,
                        street: user.street,
                        buildingNo: user.building,
                        apartmentNo: user.apartment,
                        postalCode: user.postalCode,
                        town: user.town,
                        nip: String(user.nip),
                        pesel: user.pesel,
                        treasuryOffice: user.taxOffice,
                        phone: String(user.phone),
                        email: user.email
                    },
                    company: {
                        name: user.company,
                        regon: String(user.regon)
                    }
                };
            }
            store.dispatch(dataActions.setUserInfo(setUser));

            return true;
        }
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setUserInfoFetching(false);
    }
};

export const updateUserData = async (
    login: string,
    type: string,
    user: UserRegistrationInfo
): Promise<boolean> => {
    const route: string = 'user/updateUserData';
    setUserInfoFetching(true);
    const pesel: string = user.user.pesel === undefined ? '' : user.user.pesel;
    const taxOffice: string = user.user.treasuryOffice === undefined ? '' : user.user.treasuryOffice;
    const stateUser = userService.getUser();
    if (stateUser === null) return false;
    try {
        const data = await put<UserUpdateResponse>(`${API_ADDRESS}${route}`, {
            login,
            name: user.user.firstName,
            surname: user.user.lastName,
            street: user.user.street,
            building: user.user.buildingNo,
            apartment: user.user.apartmentNo,
            postalCode: user.user.postalCode,
            town: user.user.town,
            nip: user.user.nip,
            regon: user.company.regon,
            pesel,
            taxOffice,
            phone: user.user.phone,
            email: user.user.email,
            company: user.company.name,
            type: type === 'trader' ? 2 : 1
        });
        await getUserInfo(login);
        if (data.parsedBody?.res === 'OK.') {
            store.dispatch(sessionActions.setUser({
                ...stateUser,
                name: user.user.firstName,
                surname: user.user.lastName,
                email: user.user.email,
                phone: user.user.phone ?? stateUser.phone,
                token: data.parsedBody.token
            }));
            return true;
        } return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    } finally {
        setUserInfoFetching(false);
    }
};

export const getLogin = async (
    login: string,
    password: string
): Promise<any> => {
    const route: string = 'login';
    setUserInfoFetching(true);
    try {
        const data = await post<LoginResponse | ErrorResponse>(`${API_ADDRESS}${route}`, {
            login,
            password // CryptoJS.MD5(password).toString(),
        });
        if (data.ok && data.parsedBody) {
            if ('error' in data.parsedBody) return (data.parsedBody as ErrorResponse);
            GA_userLoggedIn();
            return mapLoginResponseToUser(data.parsedBody as LoginResponse);
        } throw new Error();
    } catch (err) {
        console.log('Error ', err);
        throw err;
    } finally {
        setUserInfoFetching(false);
    }
};

export const changePassword = async (
    oldPassword: string,
    newPassword: string
): Promise<string | null> => {
    const route: string = 'changePassword';
    const user = userService.getUser();
    if (user === null) return null;
    setUserInfoFetching(true);
    const { login, logo } = user;
    try {
        const data = await post<Response | ErrorResponse>(`${API_ADDRESS}${route}`, {
            login,
            logo,
            oldPassword,
            newPassword
        });
        if (!data.parsedBody) throw new Error('Podczas zmiany hasła wystąpił błąd');
        if (typeof data.parsedBody === 'object' && 'error' in data.parsedBody) return (data.parsedBody as ErrorResponse).error;
        return '';
    } catch (err) {
        console.log('Error ', err);
        throw err;
    } finally {
        setUserInfoFetching(false);
    }
};

export const resetPassword = async (
    login: string,
    phone: string
) => {
    const route: string = 'resetPassword';
    try {
        const data = await post<RegisteredUserData>(`${API_ADDRESS}${route}`, {
            login,
            phone
        });
        return data.parsedBody;
    } catch (err) {
        console.log('Error ', err);
        throw err;
    }
};

export const getRoofer = async (
    login: string
): Promise<RooferInfo> => {
    const route: string = 'user/getRooferInfo';
    setUserInfoFetching(true);
    try {
        const data = await post<RooferInfoResponse>(`${API_ADDRESS}${route}`, { rooferLogin: login });

        if (data.ok && data.parsedBody) {
            return data.parsedBody as RooferInfo;
        }

        throw new Error();
    } catch (err) {
        console.log(err);

        return { serverError: true } as RooferInfo;
    } finally {
        setUserInfoFetching(false);
    }
};

export const updateUserParams = async (
    ...newParams: Param[]
): Promise<{ ok: boolean; }> => {
    const route: string = 'user/updateUserParams';
    const user = userService.getUser();
    if (user === null) return { ok: false };
    const { login, logo, params } = user;

    if (!params || !newParams) {
        return { ok: false };
    }

    setUserInfoFetching(true);

    newParams = params.map((param) => newParams.find((newParam) => newParam.ParamName === param.ParamName) || param);

    try {
        const data = await put<UserUpdateResponse>(`${API_ADDRESS}${route}`, {
            login,
            logo,
            params: newParams
        });
        if (data.parsedBody?.res === 'OK.') {
            store.dispatch(sessionActions.setUserParams(newParams));
            store.dispatch(sessionActions.setToken(data.parsedBody.token));
            sessionService.setToken(data.parsedBody.token, user.tokenExpiration);
            return { ok: true };
        }

        return { ok: false };
    } catch (err) {
        console.log('Error ', err);
        return { ok: false };
    } finally {
        setUserInfoFetching(false);
    }
};

export const getCurrentOrIncomingServiceWorks = async (): Promise<Boolean> => {
    const route: string = 'user/getCurrentOrIncomingServiceWorks';
    setUserInfoFetching(true);
    try {
        const data = await get<{ serviceWorks: ServiceWorkType, shutdownHours: ShutdownHours }>(`${API_ADDRESS}${route}`);
        if (data.ok && data.parsedBody && data.parsedBody.serviceWorks) {
            const serviceWorks = {
                id: data.parsedBody.serviceWorks.id,
                from: new Date(data.parsedBody.serviceWorks.from),
                to: new Date(data.parsedBody.serviceWorks.to),
                message: data.parsedBody.serviceWorks.message
            } as ServiceWorkType;
            const shutdownHours = { ...data.parsedBody.shutdownHours };
            store.dispatch(dataActions.setCurrentOrIncomingServiceWorks(serviceWorks));
            store.dispatch(dataActions.setShutdownHoursUserInfo(shutdownHours));
            return true;
        }
        return false;
    } catch (error) {
        console.log(error);
        return false;
    } finally {
        setUserInfoFetching(false);
    }
};

export const getUserDeliveryAddresses = async (): Promise<Boolean> => {
    const route: string = 'user/getAddresses';
    const user = userService.getUser();
    if (user === null) return false;
    const { login, logo } = user;
    try {
        store.dispatch(dataActions.clearUserDeliveryAddresses());
        const data = await post<UserDeliveryAddress[]>(`${API_ADDRESS}${route}`, {
            login,
            logo
        });
        if (data.ok && data.parsedBody) {
            const addresses = data.parsedBody.map((value) => Object.assign(new UserDeliveryAddress(), value));
            store.dispatch(dataActions.setUserDeliveryAddresses(addresses));
            return true;
        }
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    }
};

export const addUserDeliveryAddress = async (params: UserDeliveryAddress): Promise<string> => {
    const route: string = 'user/addAddress';
    const user = userService.getUser();
    if (user === null) return '';
    const { login, logo } = user;
    try {
        const data = await post<string>(`${API_ADDRESS}${route}`, {
            login,
            logo,
            streetType: params.streetType,
            streetName: params.streetName,
            houseNumber: params.houseNumber,
            apartmentNumber: params.apartmentNumber,
            zipCode: params.zipCode,
            town: params.town,
            country: params.country,
            description: params.description,
            fullAddress: params.fullAddress
        });
        if (data.ok && data.parsedBody) {
            return data.parsedBody;
        }
        return '';
    } catch (err) {
        console.log('Error ', err);
        return '';
    }
};

export const editUserDeliveryAddress = async (params: UserDeliveryAddress): Promise<string> => {
    const route: string = 'user/editAddress';
    const user = userService.getUser();
    if (user === null) return '';
    const { login, logo } = user;
    try {
        const data = await put<string>(`${API_ADDRESS}${route}`, {
            login,
            logo,
            id: params.id,
            streetType: params.streetType,
            streetName: params.streetName,
            houseNumber: params.houseNumber,
            apartmentNumber: params.apartmentNumber,
            zipCode: params.zipCode,
            town: params.town,
            country: params.country,
            description: params.description,
            fullAddress: params.fullAddress
        });
        if (data.ok && data.parsedBody) {
            return data.parsedBody;
        }
        return '';
    } catch (err) {
        console.log('Error ', err);
        return '';
    }
};

export const deleteUserDeliveryAddress = async (params: UserDeliveryAddress): Promise<Boolean> => {
    const route: string = 'user/deleteAddress';
    const user = userService.getUser();
    if (user === null) return false;
    const { login, logo } = user;
    try {
        const data = await del<UserDeliveryAddress[]>(`${API_ADDRESS}${route}`, {
            login,
            logo,
            id: params.id
        });
        if (data.ok && data.parsedBody) {
            return true;
        }
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    }
};

export const getManualExists = async (): Promise<boolean> => {
    const route: string = 'user/manualExists';
    try {
        const data = await get<{ manualExists: boolean }>(`${API_ADDRESS}${route}`);
        if (data.ok && data.parsedBody) {
            return data.parsedBody.manualExists;
        }
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    }
};

export const replaceManual = async (newManual: File): Promise<boolean> => {
    const route: string = 'user/replaceManual';
    try {
        const requestBody = new FormData();
        requestBody.append('file', newManual);
        const data = await post<Response>(`${API_ADDRESS}${route}`, requestBody);
        if (data.ok && data.parsedBody) {
            return true;
        }
        return false;
    } catch (err) {
        console.log('Error ', err);
        return false;
    }
};
